import { configFastOrder } from '@app/features/fastOrder/module/config/config';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AvailableTimeMinutes } from '@app/entities/Delivery';
import { searchShop } from '@app/entities/Shop';
import { sanitizeString } from '@app/shared/helpers/sanitizeString';
import { filterIsHideTwoHours, formingAnArraySBOPA } from '../helpers/formingAnArraySBOPA';
import { fetchOrderOneClick } from '../services/fetch-order-one-click';
import { fetchShops } from '../services/fetch-shops';
import { fastOrderSchema, PickUpShopsType, ProductType, TypeTemplateFastOrder } from '../types/fastOrderSchema';

const initialState: fastOrderSchema = {
  shops: [],
  historyShops: [],
  currentShop: null,
  product: null,
  historyRnProduct: null,
  order: null,
  transferredShopId: null,
  template: TypeTemplateFastOrder.pickUpStore,
  isAddDistance: false,
  isOpen: false,
  isSuccess: false,
  isLoadedShops: false,
  isHideShopTwoHours: false,
  isProductsIsBasket: false,
  errorData: null,
  count: 1,
  filterShops: [],
  openShopId: null,
};

export const fastOrderSlice = createSlice({
  name: 'fastOrder',
  initialState,
  reducers: {
    setOpenModal: (
      state,
      action: PayloadAction<{
        productData?: ProductType;
        template: TypeTemplateFastOrder;
        currentShopId?: number;
        isProductsIsBasket?: boolean;
      }>,
    ) => {
      state.isOpen = true;
      state.product = action.payload.productData;
      state.count = action.payload.productData?.quantity;
      state.template = action.payload.template;
      state.transferredShopId = action.payload.currentShopId;
      state.isProductsIsBasket = action.payload.isProductsIsBasket;
    },
    closeModal: state => {
      return {
        ...initialState,
        historyShops: state.historyShops,
        historyRnProduct: state.historyRnProduct,
      };
    },
    setHistoryRnProduct: (state, action: PayloadAction<number>) => {
      state.historyRnProduct = action.payload;
    },
    hideShopTwoHours: (state, action: PayloadAction<boolean>) => {
      state.isHideShopTwoHours = action.payload;
      state.shops = action.payload ? filterIsHideTwoHours([...state.historyShops]) : state.historyShops;
      if (state.currentShop?.statusDeliveryToday === AvailableTimeMinutes.CONST_TWO_HOURS) {
        state.currentShop = null;
      }
    },
    setFilterShops: (state, action: PayloadAction<string>) => {
      const searchValue = sanitizeString(action.payload);
      state.filterShops = searchValue ? searchShop(searchValue, [...state.shops]) : [...state.shops];
    },
    setCurrentShop: (state, action: PayloadAction<number>) => {
      const shop = state.shops.find(shop => shop.shopId === action.payload);
      if (shop) {
        state.currentShop = shop;
      }
    },
    addDistance: (state, action: PayloadAction<PickUpShopsType[]>) => {
      if (action.payload) {
        const array = formingAnArraySBOPA(action.payload);
        const isHideTwoHours =
          state.product.quantity < configFastOrder.minQtyIsHideShopTwoHours || state.product.price < configFastOrder.minPriceIsHideShopTwoHours;
        state.shops = isHideTwoHours ? filterIsHideTwoHours(array) : array;
        state.filterShops = isHideTwoHours ? filterIsHideTwoHours(array) : array;
        state.historyShops = array;
        state.isAddDistance = true;
      }
    },
    resetShop: state => {
      state.currentShop = null;
    },
    setCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
      const isHideShopTwoHours = action.payload < configFastOrder.minQtyIsHideShopTwoHours && !state.isHideShopTwoHours;
      state.isHideShopTwoHours = isHideShopTwoHours;
      const filterShops = isHideShopTwoHours ? filterIsHideTwoHours([...state.historyShops]) : state.historyShops;
      state.shops = filterShops;
      state.filterShops = filterShops;

      if (state.currentShop?.statusDeliveryToday === AvailableTimeMinutes.CONST_TWO_HOURS) {
        state.currentShop = null;
      }
    },
    setOpenShop: (state, action: PayloadAction<number | null>) => {
      state.openShopId = state.openShopId !== action.payload ? action.payload : null;
    },
    reset: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(fetchShops.fulfilled, (state, action) => {
      if (action.payload && state.product) {
        const isHideTwoHours =
          state.product.quantity < configFastOrder.minQtyIsHideShopTwoHours || state.product.price < configFastOrder.minPriceIsHideShopTwoHours;
        const array = formingAnArraySBOPA(action.payload);
        state.shops = isHideTwoHours ? filterIsHideTwoHours(array) : array;
        state.filterShops = isHideTwoHours ? filterIsHideTwoHours(array) : array;
        state.isHideShopTwoHours = isHideTwoHours;
        state.historyShops = array;
        if (state.transferredShopId) {
          state.currentShop = array.find(shop => shop.shopId === state.transferredShopId);
        }
        if (array.length === 1) {
          state.currentShop = array[0];
        }

        state.historyRnProduct = state.product.productRn;
      }
      state.isLoadedShops = true;
    });
    builder.addCase(fetchShops.rejected, state => {
      state.isLoadedShops = true;
    });
    builder.addCase(fetchOrderOneClick.pending, state => {
      state.errorData = null;
    });
    builder.addCase(fetchOrderOneClick.fulfilled, (state, action) => {
      if (action.payload) {
        state.order = action.payload;
      }
      state.isSuccess = true;
      state.errorData = null;
    });
    builder.addCase(fetchOrderOneClick.rejected, (state, action) => {
      state.errorData = action.payload;
      state.isSuccess = false;
    });
  },
});

export const { actions: fastOrderActions } = fastOrderSlice;
